/**
 * Fussliebe Fussbalm — landing section behaviour.
 * Vanilla JS, no build step. Scoped per section instance so the section can
 * be added more than once on a page and works correctly inside the Shopify
 * theme editor (re-inits on shopify:section:load, tears down on unload).
 */
(function () {
  'use strict';

  var instances = Object.create(null);

  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function initCountdown(root) {
    var wrap = root.querySelector('[data-fb-countdown]');
    if (!wrap) return null;
    var hh = wrap.querySelector('[data-fb-countdown-hh]');
    var mm = wrap.querySelector('[data-fb-countdown-mm]');
    var ss = wrap.querySelector('[data-fb-countdown-ss]');
    var remaining = parseInt(wrap.getAttribute('data-hours'), 10) * 3600;
    if (!isFinite(remaining) || remaining < 0) remaining = 0;

    function render() {
      var h = Math.floor(remaining / 3600);
      var m = Math.floor((remaining % 3600) / 60);
      var s = remaining % 60;
      if (hh) hh.textContent = pad(h);
      if (mm) mm.textContent = pad(m);
      if (ss) ss.textContent = pad(s);
    }

    render();
    var timer = setInterval(function () {
      if (remaining > 0) remaining -= 1;
      render();
    }, 1000);

    return function teardown() {
      clearInterval(timer);
    };
  }

  function initPlans(root) {
    var plans = Array.prototype.slice.call(root.querySelectorAll('.fb-plan'));
    if (!plans.length) return null;
    var images = Array.prototype.slice.call(root.querySelectorAll('[data-plan-image]'));
    var discountBadge = root.querySelector('[data-discount-badge]');
    var cta = root.querySelector('[data-plan-cta]');

    function select(index) {
      plans.forEach(function (btn, i) {
        var active = i === index;
        btn.classList.toggle('is-selected', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      images.forEach(function (img) {
        img.classList.toggle('is-active', img.getAttribute('data-plan-image') === String(index));
      });
      var current = plans[index];
      var discount = current.getAttribute('data-discount') || '';
      if (discountBadge) {
        if (discount) {
          discountBadge.textContent = discount;
          discountBadge.hidden = false;
        } else {
          discountBadge.hidden = true;
        }
      }
      if (cta) {
        var url = current.getAttribute('data-checkout-url') || '#';
        cta.setAttribute('href', url);
      }
    }

    var clickHandler = function (e) {
      var btn = e.currentTarget;
      var index = plans.indexOf(btn);
      select(index);
    };
    plans.forEach(function (btn) {
      btn.addEventListener('click', clickHandler);
    });

    var initialIndex = plans.findIndex(function (btn) {
      return btn.getAttribute('data-selected') === 'true';
    });
    select(initialIndex >= 0 ? initialIndex : 0);

    return function teardown() {
      plans.forEach(function (btn) {
        btn.removeEventListener('click', clickHandler);
      });
    };
  }

  function initFaq(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll('.fb-faq-item'));
    if (!items.length) return null;
    var handlers = [];

    items.forEach(function (item) {
      var q = item.querySelector('.fb-faq-item__q');
      var icon = item.querySelector('.fb-faq-item__icon');
      if (!q) return;
      var handler = function () {
        var willOpen = !item.classList.contains('is-open');
        items.forEach(function (other) {
          other.classList.remove('is-open');
          var otherIcon = other.querySelector('.fb-faq-item__icon');
          if (otherIcon) otherIcon.textContent = '+';
          var otherQ = other.querySelector('.fb-faq-item__q');
          if (otherQ) otherQ.setAttribute('aria-expanded', 'false');
        });
        if (willOpen) {
          item.classList.add('is-open');
          if (icon) icon.textContent = '–';
          q.setAttribute('aria-expanded', 'true');
        }
      };
      q.addEventListener('click', handler);
      handlers.push([q, handler]);
    });

    return function teardown() {
      handlers.forEach(function (pair) {
        pair[0].removeEventListener('click', pair[1]);
      });
    };
  }

  function initReviews(root) {
    var toggle = root.querySelector('.fb-reviews__toggle');
    if (!toggle) return null;
    var extra = Array.prototype.slice.call(root.querySelectorAll('[data-review-extra]'));
    var labelMore = toggle.getAttribute('data-label-more') || 'Weitere Bewertungen anzeigen';
    var labelLess = toggle.getAttribute('data-label-less') || 'Weniger anzeigen';
    var labelEl = toggle.querySelector('[data-toggle-label]');

    var handler = function () {
      var expanded = toggle.classList.toggle('is-expanded');
      extra.forEach(function (el) {
        el.hidden = !expanded;
      });
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (labelEl) labelEl.textContent = expanded ? labelLess : labelMore;
    };
    toggle.addEventListener('click', handler);

    return function teardown() {
      toggle.removeEventListener('click', handler);
    };
  }

  function initSticky(root) {
    var sticky = root.querySelector('[data-fb-sticky]');
    var trigger = root.querySelector('[data-sticky-trigger]');
    if (!sticky || !trigger) return null;

    var ticking = false;
    function update() {
      var past = trigger.getBoundingClientRect().bottom < 0;
      sticky.classList.toggle('is-visible', past);
      ticking = false;
    }
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return function teardown() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  function initSection(root) {
    var teardowns = [
      initCountdown(root),
      initPlans(root),
      initFaq(root),
      initReviews(root),
      initSticky(root)
    ].filter(Boolean);

    return function teardownAll() {
      teardowns.forEach(function (fn) { fn(); });
    };
  }

  function boot(root) {
    var id = root.getAttribute('data-section-id');
    if (!id || instances[id]) return;
    instances[id] = initSection(root);
  }

  function shutdown(id) {
    if (instances[id]) {
      instances[id]();
      delete instances[id];
    }
  }

  document.querySelectorAll('[data-fb-landing]').forEach(boot);

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fb-landing]');
    if (root) boot(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-fb-landing]');
    if (root) shutdown(root.getAttribute('data-section-id'));
  });
})();
